import { create } from 'zustand';

const useAudioStore = create((set, get) => ({
  // ============ STATE ============
  
  // Player status
  status: 'idle', // 'idle' | 'loading' | 'playing' | 'paused' | 'ended'
  
  // Playback mode
  mode: null, // null | 'single-ayah' | 'full-surah'
  
  // Current position
  currentSurah: null,
  currentAyah: null,
  totalAyahs: null,
  
  // Surah metadata
  surahData: null, // { name, englishName, numberOfAyahs, etc. }
  
  // Audio timing
  currentTime: 0,
  duration: 0,
  progress: 0,
  
  // Settings
  repeat: 'off', // 'off' | 'ayah' | 'surah' | 'all'
  reciter: 'ar.alafasy',
  volume: 1,
  playbackSpeed: 1.0, // 0.75, 1.0, or 1.25
  
  // UI state
  isExpanded: false,
  
  // SINGLE audio element reference (set from AudioProvider)
  audioRef: null,
  
  // Heartbeat interval for periodic persistence
  heartbeatInterval: null,
  
  // ============ ACTIONS ============
  
  // Initialize single audio ref
  setAudioRef: (ref) => set({ audioRef: ref }),
  
  // Start heartbeat for periodic persistence (every 10 seconds)
  startHeartbeat: () => {
    const { heartbeatInterval } = get();
    
    // Clear existing interval if any
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
    
    // Save position every 10 seconds during active playback
    const interval = setInterval(() => {
      const { status } = get();
      if (status === 'playing') {
        get().saveToLocalStorage();
      }
    }, 10000); // 10 seconds
    
    set({ heartbeatInterval: interval });
  },
  
  // Stop heartbeat
  stopHeartbeat: () => {
    const { heartbeatInterval } = get();
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      set({ heartbeatInterval: null });
    }
  },
  
  // Play full surah
  playFullSurah: async (surahNumber) => {
    const { audioRef } = get();
    
    if (!audioRef?.current) {
      console.error('Audio ref not initialized');
      return;
    }
    
    // Stop current playback cleanly
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    
    set({ 
      status: 'loading',
      mode: 'full-surah',
      currentSurah: surahNumber,
      currentAyah: 1,
      currentTime: 0,
      duration: 0,
      progress: 0,
    });
    
    // Fetch surah metadata
    try {
      const response = await fetch(`/api/surahs/${surahNumber}`);
      const data = await response.json();
      set({ 
        surahData: data.data,
        totalAyahs: data.data.numberOfAyahs 
      });
    } catch (err) {
      console.error('Failed to fetch surah data:', err);
    }
    
    // Load and play - SINGLE audio element
    audioRef.current.src = `https://cdn.islamic.network/quran/audio-surah/128/${get().reciter}/${surahNumber}.mp3`;
    
    try {
      await audioRef.current.play();
      set({ status: 'playing' });
      get().saveToLocalStorage();
      get().startHeartbeat(); // Start periodic saving
    } catch (err) {
      console.error('Play failed:', err);
      set({ status: 'idle' });
    }
  },
  
  // Play single ayah
  playAyah: async (surahNumber, ayahNumber, absoluteAyahNumber) => {
    const { audioRef } = get();
    
    if (!audioRef?.current) {
      console.error('Audio ref not initialized');
      return;
    }
    
    // Stop current playback cleanly
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    
    set({ 
      status: 'loading',
      mode: 'single-ayah',
      currentSurah: surahNumber,
      currentAyah: ayahNumber,
      currentTime: 0,
      duration: 0,
      progress: 0,
    });
    
    // Fetch surah metadata if not loaded
    const { surahData } = get();
    if (!surahData || surahData.number !== surahNumber) {
      try {
        const response = await fetch(`/api/surahs/${surahNumber}`);
        const data = await response.json();
        set({ 
          surahData: data.data,
          totalAyahs: data.data.numberOfAyahs 
        });
      } catch (err) {
        console.error('Failed to fetch surah data:', err);
      }
    }
    
    // Load and play - SINGLE audio element
    audioRef.current.src = `https://cdn.islamic.network/quran/audio/128/${get().reciter}/${absoluteAyahNumber}.mp3`;
    
    try {
      await audioRef.current.play();
      set({ status: 'playing' });
      get().saveToLocalStorage();
      get().startHeartbeat(); // Start periodic saving
    } catch (err) {
      console.error('Play failed:', err);
      set({ status: 'idle' });
    }
  },
  
  // Toggle play/pause
  togglePlayPause: () => {
    const { status, audioRef } = get();
    
    if (!audioRef?.current) return;
    
    if (status === 'playing') {
      audioRef.current.pause();
      set({ status: 'paused' });
      get().stopHeartbeat(); // Stop periodic saving
      get().saveToLocalStorage(); // Save immediately on pause
    } else if (status === 'paused') {
      audioRef.current.play();
      set({ status: 'playing' });
      get().startHeartbeat(); // Resume periodic saving
    }
  },
  
  // Next ayah/surah
  nextTrack: async () => {
    const { mode, currentSurah, currentAyah, totalAyahs } = get();
    
    if (mode === 'single-ayah') {
      // Move to next ayah in same surah
      if (currentAyah < totalAyahs) {
        const nextAyah = currentAyah + 1;
        const absoluteAyahNumber = await get().calculateAbsoluteAyahNumber(currentSurah, nextAyah);
        await get().playAyah(currentSurah, nextAyah, absoluteAyahNumber);
      }
    } else if (mode === 'full-surah') {
      // Can't skip within full surah - would need to restart
      console.log('Skip not supported in full surah mode');
    }
  },
  
  // Previous ayah/surah
  prevTrack: async () => {
    const { mode, currentSurah, currentAyah } = get();
    
    if (mode === 'single-ayah') {
      // Move to previous ayah
      if (currentAyah > 1) {
        const prevAyah = currentAyah - 1;
        const absoluteAyahNumber = await get().calculateAbsoluteAyahNumber(currentSurah, prevAyah);
        await get().playAyah(currentSurah, prevAyah, absoluteAyahNumber);
      }
    }
  },
  
  // Seek to time
  seek: (time) => {
    const { audioRef } = get();
    if (audioRef?.current) {
      audioRef.current.currentTime = time;
    }
  },
  
  // Set volume
  setVolume: (volume) => {
    const { audioRef } = get();
    if (audioRef?.current) {
      audioRef.current.volume = volume;
      set({ volume });
    }
  },
  
  // Toggle repeat
  toggleRepeat: () => {
    const { repeat } = get();
    const modes = ['off', 'ayah', 'surah'];
    const currentIndex = modes.indexOf(repeat);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    set({ repeat: nextMode });
  },
  
  // Set playback speed
  setPlaybackSpeed: (speed) => {
    const { audioRef } = get();
    if (audioRef?.current) {
      audioRef.current.playbackRate = speed;
    }
    set({ playbackSpeed: speed });
  },
  
  // Set reciter
  setReciter: async (reciter) => {
    const { currentSurah, currentAyah, mode, status } = get();
    const wasPlaying = status === 'playing';
    
    // Update reciter
    set({ reciter });
    
    // If audio was playing, reload with new reciter
    if (wasPlaying && currentSurah) {
      const store = get();
      if (mode === 'full-surah') {
        store.playFullSurah(currentSurah);
      } else if (mode === 'single-ayah' && currentAyah) {
        const absoluteAyahNumber = await store.calculateAbsoluteAyahNumber(currentSurah, currentAyah);
        await store.playAyah(currentSurah, currentAyah, absoluteAyahNumber);
      }
    }
  },
  
  // Toggle player expansion
  toggleExpanded: () => set((state) => ({ isExpanded: !state.isExpanded })),
  
  // Stop audio
  stop: () => {
    const { audioRef } = get();
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    get().stopHeartbeat(); // Stop periodic saving
    get().saveToLocalStorage(); // Save immediately on stop
    set({ status: 'idle' });
  },
  
  // Close player completely
  closePlayer: () => {
    const { audioRef } = get();
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    get().stopHeartbeat(); // Stop periodic saving
    localStorage.removeItem('quran-audio-state'); // Clear saved state
    set({ 
      status: 'idle',
      mode: null,
      currentSurah: null,
      currentAyah: null,
      surahData: null,
      currentTime: 0,
      duration: 0,
      progress: 0,
      isExpanded: false,
    });
  },
  
  // Handle audio ended
  handleEnded: async () => {
    const { mode, repeat, currentSurah, currentAyah } = get();
    
    // Stop heartbeat and clear saved position when track ends naturally
    get().stopHeartbeat();
    localStorage.removeItem('quran-audio-state');
    
    if (mode === 'single-ayah') {
      if (repeat === 'ayah') {
        // Replay same ayah
        const absoluteAyahNumber = await get().calculateAbsoluteAyahNumber(currentSurah, currentAyah);
        await get().playAyah(currentSurah, currentAyah, absoluteAyahNumber);
      } else {
        // Stop
        set({ status: 'ended' });
      }
    } else if (mode === 'full-surah') {
      if (repeat === 'surah') {
        // Replay same surah
        await get().playFullSurah(currentSurah);
      } else {
        // Stop
        set({ status: 'ended' });
      }
    }
  },
  
  // Update time
  updateTime: (currentTime, duration) => {
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    set({ currentTime, duration, progress });
  },
  
  // Calculate absolute ayah number
  calculateAbsoluteAyahNumber: async (surahNumber, ayahNumber) => {
    // This would need a lookup table or API call
    // For now, approximate (you'll need to implement proper lookup)
    try {
      const response = await fetch(`/api/surahs/${surahNumber}`);
      const data = await response.json();
      const surah = data.data;
      
      if (surah.ayahs && surah.ayahs[ayahNumber - 1]) {
        return surah.ayahs[ayahNumber - 1].number;
      }
    } catch (err) {
      console.error('Failed to get absolute ayah number:', err);
    }
    
    // Fallback (won't be accurate)
    return ayahNumber;
  },
  
  // LocalStorage persistence
  saveToLocalStorage: () => {
    const { currentSurah, currentAyah, mode, currentTime, duration, audioRef } = get();
    
    // Get the most accurate current time directly from the audio element
    const actualTime = audioRef?.current?.currentTime || currentTime;
    
    localStorage.setItem('quran-audio-state', JSON.stringify({
      surah: currentSurah,
      ayah: currentAyah,
      mode: mode,
      time: actualTime,
      duration: duration,
      timestamp: Date.now(),
    }));
  },
  
  loadFromLocalStorage: () => {
    try {
      const saved = localStorage.getItem('quran-audio-state');
      if (saved) {
        const data = JSON.parse(saved);
        // Return data for resume button
        return data;
      }
    } catch (err) {
      console.error('Failed to load from localStorage:', err);
    }
    return null;
  },
  
  resumeFromLocalStorage: async () => {
    const saved = get().loadFromLocalStorage();
    if (saved && saved.mode !== 'revelation-order') {
      if (saved.mode === 'full-surah') {
        await get().playFullSurah(saved.surah);
        // Seek to saved time
        setTimeout(() => {
          get().seek(saved.time);
        }, 500);
      } else if (saved.mode === 'single-ayah') {
        const absoluteAyahNumber = await get().calculateAbsoluteAyahNumber(saved.surah, saved.ayah);
        await get().playAyah(saved.surah, saved.ayah, absoluteAyahNumber);
      }
    }
  },
}));

export default useAudioStore;
